import { useCallback, useMemo, useState } from "react";
import { TournamentStyle, TournamentVisibility } from "../../../../generated/bpong_pb";
import { TeamForm } from "../types";

export const createEmptyTeam = (id: number): TeamForm => ({
    id,
    name: "",
    participants: [""],
});

interface NormalizedTeam {
    id: number;
    teamName: string;
    participants: string[];
}

interface UseTournamentFormResult {
    tournamentName: string;
    teams: TeamForm[];
    selectedStyle: TournamentStyle;
    selectedVisibility: TournamentVisibility;
    accessPassword: string;
    setTournamentName: (name: string) => void;
    setSelectedStyle: (style: TournamentStyle) => void;
    setSelectedVisibility: (visibility: TournamentVisibility) => void;
    setAccessPassword: (value: string) => void;
    addTeam: () => void;
    removeTeam: (id: number) => void;
    updateTeamName: (id: number, name: string) => void;
    addParticipant: (teamId: number) => void;
    updateParticipant: (teamId: number, index: number, value: string) => void;
    removeParticipant: (teamId: number, index: number) => void;
    resetForm: () => void;
    normalizedTeams: NormalizedTeam[];
    validTeams: NormalizedTeam[];
}

export const useTournamentForm = (defaultStyle: TournamentStyle): UseTournamentFormResult => {
    const [tournamentName, setTournamentName] = useState<string>("");
    const [teams, setTeams] = useState<TeamForm[]>([createEmptyTeam(1), createEmptyTeam(2)]);
    const [nextTeamId, setNextTeamId] = useState<number>(3);
    const [selectedStyle, setSelectedStyle] = useState<TournamentStyle>(defaultStyle);
    const [selectedVisibility, setSelectedVisibility] = useState<TournamentVisibility>(
        TournamentVisibility.TOURNAMENT_VISIBILITY_PUBLIC
    );
    const [accessPassword, setAccessPassword] = useState<string>("");

    const addTeam = useCallback(() => {
        setTeams((prev) => [...prev, createEmptyTeam(nextTeamId)]);
        setNextTeamId((prev) => prev + 1);
    }, [nextTeamId]);

    const removeTeam = useCallback((id: number) => {
        setTeams((prev) => prev.filter((team) => team.id !== id));
    }, []);

    const updateTeamName = useCallback((id: number, name: string) => {
        setTeams((prev) =>
            prev.map((team) => (team.id === id ? { ...team, name } : team))
        );
    }, []);

    const addParticipant = useCallback((teamId: number) => {
        setTeams((prev) =>
            prev.map((team) =>
                team.id === teamId
                    ? {
                          ...team,
                          participants: [...team.participants, ""],
                      }
                    : team
            )
        );
    }, []);

    const updateParticipant = useCallback((teamId: number, index: number, value: string) => {
        setTeams((prev) =>
            prev.map((team) =>
                team.id === teamId
                    ? {
                          ...team,
                          participants: team.participants.map((participant, idx) =>
                              idx === index ? value : participant
                          ),
                      }
                    : team
            )
        );
    }, []);

    const removeParticipant = useCallback((teamId: number, index: number) => {
        setTeams((prev) =>
            prev.map((team) =>
                team.id === teamId
                    ? {
                          ...team,
                          participants:
                              team.participants.length <= 1
                                  ? team.participants
                                  : team.participants.filter((_, idx) => idx !== index),
                      }
                    : team
            )
        );
    }, []);

    const resetForm = useCallback(() => {
        setTournamentName("");
        setTeams([createEmptyTeam(1), createEmptyTeam(2)]);
        setNextTeamId(3);
        setSelectedStyle(defaultStyle);
        setSelectedVisibility(TournamentVisibility.TOURNAMENT_VISIBILITY_PUBLIC);
        setAccessPassword("");
    }, [defaultStyle]);

    const normalizedTeams = useMemo<NormalizedTeam[]>(
        () =>
            teams.map((team) => ({
                id: team.id,
                teamName: team.name.trim(),
                participants: team.participants.map((p) => p.trim()).filter(Boolean),
            })),
        [teams]
    );

    const validTeams = useMemo(
        () => normalizedTeams.filter((team) => team.teamName.length > 0),
        [normalizedTeams]
    );

    return {
        tournamentName,
        teams,
        selectedStyle,
        selectedVisibility,
        accessPassword,
        setTournamentName,
        setSelectedStyle,
        setSelectedVisibility,
        setAccessPassword,
        addTeam,
        removeTeam,
        updateTeamName,
        addParticipant,
        updateParticipant,
        removeParticipant,
        resetForm,
        normalizedTeams,
        validTeams,
    };
};
